import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";

import { KeyValue } from "@/main/models/common";
import { getTranslateByKey, isNullUndefined, isValueQueryParams } from "@/main/services/utils";
import { useDinamicFiltersMappingCodes } from "./dinamicFiltersMappingCodes";
import { getCompanyType, useCompanyTypeReducer } from "@/main/store/companyType";
import { getCompany, resetReducerCompany, useCompanyReducer } from "@/main/store/company";
import { useDispatch } from "react-redux";
import { StatusRegisterEnum } from "@/main/services/enum/StatusRegisterEnum";
import { CompanyTypeEnum } from "@/main/services/enum/CompanyTypeEnum";
import { getAddress, useAddressReducer } from "@/main/store/address";

export const INITIAL_FORM_STATE_ADDRESS = {
	companyType: null as KeyValue | null,
	company: null as KeyValue | null,
	address: null as KeyValue | null,
};

export type IFormFiltersMappingTypeAddress = typeof INITIAL_FORM_STATE_ADDRESS;

interface IFiltersMappingTypeAddressProps {}

function FiltersMappingTypeAddress(_props: IFiltersMappingTypeAddressProps) {
	//#region Hooks
	const dispatch = useDispatch();
	const { isEdit, keyReducer, watcherCountry, isRequiredField } = useDinamicFiltersMappingCodes();
	const { control, resetField, setValue } = useFormContext<IFormFiltersMappingTypeAddress>();
	//#endregion

	//#region Reducers
	const { companyType: companyTypeReducer } = useCompanyTypeReducer();
	const { isolated: companyReducer } = useCompanyReducer(keyReducer);
	const { isolated: addressReducer } = useAddressReducer(keyReducer);

	//#endregion

	// #region States
	const watcherCompanyType = useWatch({
		control,
		name: "companyType",
	});

	const watcherCompany = useWatch({
		control,
		name: "company",
	});

	const skipFirstRender = React.useRef(true);

	//#endregion

	// #region Async Functions

	async function callCompanyTypeApiDomain() {
		dispatch(getCompanyType());
	}
	async function callCompanyApiDomain(companyTypeSelected: KeyValue | null) {
		if (!isValueQueryParams(companyTypeSelected) && skipFirstRender.current == false) {
			setValue("company", null);
		}

		if (isNullUndefined(companyTypeSelected) || isNullUndefined(watcherCountry)) return;
		skipFirstRender.current = false;
		dispatch(
			getCompany(
				{
					guidCountry: String(watcherCountry.key),
					idCompanyType: String(companyTypeSelected.key),
					idStatusRegister: StatusRegisterEnum.Active,
				},
				keyReducer,
			),
		);
	}

	async function callAddressApiDomain(companySelected: KeyValue | null) {
		if (!isValueQueryParams(companySelected)) {
			resetField("address");
		}

		if (isNullUndefined(companySelected) || isNullUndefined(watcherCountry)) return;

		dispatch(
			getAddress(
				{
					guidCompany: String(companySelected.key),
				},
				keyReducer,
			),
		);
	}
	// #endregion

	useEffect(() => {
		if (isNullUndefined(companyTypeReducer.data)) callCompanyTypeApiDomain();

		return () => {
			dispatch(resetReducerCompany());
		};
	}, []);

	useEffect(() => {
		callCompanyApiDomain(watcherCompanyType);
	}, [watcherCountry?.key, watcherCompanyType?.key]);

	const disabledAddress =
		isNullUndefined(watcherCompany) || isNullUndefined(addressReducer.data) || addressReducer.data.length === 1;
	const disabledCompany =
		isNullUndefined(watcherCompanyType) ||
		isNullUndefined(watcherCountry) ||
		isNullUndefined(companyReducer.data) ||
		companyReducer.data.length === 1;
	const onlyShowCompanyTypes = [CompanyTypeEnum.Insurer, CompanyTypeEnum.Supplier, CompanyTypeEnum.BodyShop];
	const optionsFilteredCompanyType = isNullUndefined(companyTypeReducer.data)
		? null
		: companyTypeReducer.data
				.filter((item) => onlyShowCompanyTypes.includes(Number(item.key)))
				.sort((a, b) => a.value.localeCompare(b.value));

	return (
		<>
			<KogAutoCompleteControlled
				id="id-mappingCode-select-companyType"
				nameCustom="companyType"
				label={getTranslateByKey("information.companyType")}
				isDefaultValueUnique
				control={control}
				setValueCustom={setValue}
				options={optionsFilteredCompanyType}
				isLoading={companyTypeReducer.isLoading}
				rulesCustom={{ required: isRequiredField("companyType") }}
				onChangeCustom={() => {}}
			/>
			<KogAutoCompleteControlled
				id="id-mappingCode-select-company"
				nameCustom="company"
				label={getTranslateByKey("information.company")}
				isDefaultValueUnique
				control={control}
				setValueCustom={setValue}
				options={companyReducer.data}
				isLoading={companyReducer.isLoading}
				disabled={disabledCompany}
				rulesCustom={{ required: isRequiredField("company") }}
				onChangeCustom={(optionSelected) => {
					callAddressApiDomain(optionSelected);
				}}
			/>
			<KogAutoCompleteControlled
				id="id-mappingCode-select-address"
				nameCustom="address"
				label={getTranslateByKey("information.address")}
				isDefaultValueUnique
				control={control}
				setValueCustom={setValue}
				options={addressReducer.data}
				isLoading={addressReducer.isLoading}
				disabled={isEdit || disabledAddress}
				rulesCustom={{ required: isRequiredField("address") }}
				onChangeCustom={() => {}}
			/>
		</>
	);
}

export default FiltersMappingTypeAddress;
